import { DomainSchema } from '@multitec/domain'
import { useQuery } from 'react-query'
import { z } from 'zod'

import { Base } from '../base'
import { MyURLSearchParams } from '../utils/url-search-params'

class Create extends Base {
  private body = z.object({
    contractId: z.number(),
    startAt: z.coerce.date(),
    finishAt: z.coerce.date(),
    description: z.string().optional(),
  })

  private response = z.object({
    id: z.number(),
  })

  handle = async (body: z.infer<typeof this.body>) => {
    const { data } = await this.axios.post(
      '/entity/contract-renovations/',
      body,
    )
    return this.response.parse(data.result)
  }
}

class Edit extends Base {
  private body = z.object({
    description: z.string(),
  })

  private response = z.object({
    id: z.number(),
  })

  handle = async (id: string | number, body: z.infer<typeof this.body>) => {
    const { data } = await this.axios.put(
      '/entity/contract-renovations/' + id,
      body,
    )
    return this.response.parse(data.result)
  }
}

class SetStatusClosed extends Base {
  private response = z.object({
    id: z.number(),
  })

  handle = async (id: string | number) => {
    const { data } = await this.axios.put(
      `/entity/contract-renovations/${id}/end`,
    )
    return this.response.parse(data.result)
  }
}

class LoadByContract extends Base {
  private response = z.array(DomainSchema.ContractRenovation)

  handle = (contractId: string | number) => {
    return useQuery(
      ['contract-renovation', 'load-by-contract', { contractId }],
      async () => {
        const queryParams = new MyURLSearchParams({ contractId }).toString()
        const { data } = await this.axios.get(
          '/entity/contract-renovations?' + queryParams,
        )
        return this.response.parse(data.result)
      },
    )
  }
}

class LoadOne extends Base {
  private response = DomainSchema.ContractRenovation

  handle = (id: string | number) => {
    return useQuery(['contract-renovation', 'load-one', { id }], async () => {
      const { data } = await this.axios.get(
        '/entity/contract-renovations/' + id,
      )
      return this.response.parse(data.result)
    })
  }
}

export default class extends Base {
  Create = new Create(this.axios).handle
  Edit = new Edit(this.axios).handle
  LoadByContract = new LoadByContract(this.axios).handle
  LoadOne = new LoadOne(this.axios).handle
  SetStatusClosed = new SetStatusClosed(this.axios).handle
}
